import { GridColDef } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { EditLeaderboardCell } from 'src/components/tournaments/heats-leaderboards/EditLeaderboardCell';
import { Prize } from 'src/components/tournaments/heats-leaderboards/Prize';
import { Qualified } from 'src/components/tournaments/heats-leaderboards/Qualified';
import { Rank } from 'src/components/tournaments/heats-leaderboards/Rank';
import { Score } from 'src/components/tournaments/heats-leaderboards/Score';
import { Username } from 'src/components/tournaments/heats-leaderboards/Username';
import { optionalColumns } from 'src/components/tournaments/heats-leaderboards/optionalColumns';
import { Details } from 'src/components/tournaments/heats-leaderboards/referee/Details';
import { useReferee } from 'src/contexts/RefereeContext';

export const useLeaderboardColumns = ({
  resultsLength,
  qualification,
  scoreOption,
  scoreOptions,
}) => {
  const { isRefereeView } = useReferee();
  const prizeColumn: GridColDef = useMemo(() => {
    return {
      field: 'prize',
      headerName: 'PRIZE',
      width: 250,
      sortable: false,
      editable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: ({ row }) => {
        return <Prize prize={row.prize} rank={row.rank} />;
      },
    };
  }, []);
  const rankColumn: GridColDef = useMemo(() => {
    return {
      field: 'rank',
      headerName: 'RANK',
      minWidth: 60,
      maxWidth: 100,
      flex: 1,
      sortable: false,
      editable: false,
      renderCell: ({ row }) => {
        return <Rank rank={row.rank} />;
      },
    };
  }, []);
  const usernameColumn: GridColDef = useMemo(() => {
    return {
      field: 'username',
      headerName: 'USERNAME',
      minWidth: 280,
      flex: 1,
      sortable: false,
      editable: false,
      renderCell: ({ row }) => {
        return (
          <Username
            imgUrl={row.imgUrl}
            name={row.name}
            rank={row.rank}
            userIdCaptain={row.userIdCaptain}
          />
        );
      },
    };
  }, []);

  const detailsColumn: GridColDef = useMemo(() => {
    return {
      field: 'details',
      headerName: 'DETAILS',
      minWidth: 600,
      maxWidth: 600,
      hideable: true,
      sortable: false,
      editable: false,
      renderCell: ({ row }) => {
        return <Details userIds={row.userIds} captainId={row.userIdCaptain} />;
      },
    };
  }, []);
  const qualifiedColumn: GridColDef = useMemo(() => {
    return {
      field: 'qualified',
      headerName: 'QUALIFIED',
      sortable: false,
      editable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: ({ row }) => {
        return (
          <Qualified
            scoreValue={row.score}
            rank={row.rank}
            resultsLength={resultsLength}
            qualification={qualification}
          />
        );
      },
    };
  }, [qualification, resultsLength]);
  const scoreOptionColumn: GridColDef = useMemo(() => {
    return {
      field: 'score',
      headerName: scoreOption?.name.toUpperCase() || 'SCORE',
      minWidth: 130,
      maxWidth: 350,
      flex: 1,
      sortable: false,
      editable: false,
      renderCell: ({ row }) => {
        return (
          <Score
            score={String(row.score)}
            rank={row.rank}
            scoreOption={scoreOption}
          />
        );
      },
    };
  }, [scoreOption]);
  // This memoization is necessary to prevent the updates to rows changing
  // the visibility & other state of the columns
  const columns = useMemo(() => {
    return [
      rankColumn,
      usernameColumn,
      ...(isRefereeView ? [detailsColumn] : []),
      scoreOptionColumn,
      ...optionalColumns({ scoreOptions }),
      ...(!!qualification ? [qualifiedColumn] : []),
      prizeColumn,
    ].map((column) => {
      return {
        ...column,
        editable: true,
        renderEditCell: (params) => {
          return (
            <EditLeaderboardCell
              {...params}
              id={params.id}
              value={params.row.score}
              field={'score'}
            />
          );
        },
      };
    });
  }, [
    detailsColumn,
    isRefereeView,
    prizeColumn,
    qualification,
    qualifiedColumn,
    rankColumn,
    scoreOptionColumn,
    scoreOptions,
    usernameColumn,
  ]);
  return columns;
};
